/**
 * Bolt - Performance diagnostics for React/Apollo applications
 *
 * Uses BoltEvents for comprehensive performance tracking and analysis.
 *
 * ## Usage
 *
 * ### For Humans (browser console):
 *   bolt.startInteraction('my-action')
 *   // ... perform action
 *   bolt.endInteraction()
 *   bolt.getReport()  // formatted summary
 *
 * ### For Agents (file-based):
 *   tail -f apps/oak/bolt-events.jsonl
 *
 * ## API
 *   startInteraction(action) - Begin tracking a user interaction
 *   endInteraction() - End the current interaction
 *   emit(input) - Emit a custom event
 *   logRender(opts) - Track component render with causality
 *   logQueryStart(opts) - Track query initiation
 *   logQueryResolve(opts) - Track query completion
 *   getReport() - Get formatted text report
 */
import { v4 as uuidv4 } from "uuid";
import { BoltEventType, EventThread, } from "./types.js";
// =============================================================================
// Storage
// =============================================================================
class BoltStorage {
    constructor(maxEvents = 10000) {
        this.events = [];
        this.maxEvents = maxEvents;
    }
    write(event) {
        this.events.push(event);
        // Trim if over limit
        if (this.events.length > this.maxEvents) {
            this.events = this.events.slice(-this.maxEvents);
        }
        // Also write to file in dev
        if (typeof window !== "undefined" && window.__BOLT_FILE_WRITER__) {
            window.__BOLT_FILE_WRITER__(JSON.stringify(event));
        }
    }
    getAll() {
        return [...this.events];
    }
    getByInteraction(interactionId) {
        return this.events.filter((e) => e.interactionId === interactionId);
    }
    clear() {
        this.events = [];
    }
    getStats() {
        const byType = new Map();
        for (const e of this.events) {
            byType.set(e.eventType, (byType.get(e.eventType) ?? 0) + 1);
        }
        return {
            total: this.events.length,
            byType: Object.fromEntries(byType),
        };
    }
}
// =============================================================================
// Bolt Class
// =============================================================================
export class Bolt {
    constructor(config = {}) {
        this.longTaskObserver = null;
        // Interaction tracking
        this.currentInteractionId = "";
        this.currentAction = "";
        this.interactionStartTime = 0;
        this.ttiMarked = false;
        // Commit tracking
        this.currentCommitId = "";
        this.commitSequence = 0;
        // Component filtering
        this.watchedComponents = new Set();
        this.alwaysTrackWatched = false;
        this.sessionId = config.sessionId ?? uuidv4();
        this.pageUrl = typeof window !== "undefined" ? window.location.href : "";
        this.enabled =
            config.enabled ??
                (typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : true);
        this.storage = new BoltStorage(config.maxEvents);
        this.clearOnNavigate = config.clearOnNavigate ?? false;
        this.autoInteractions = config.autoInteractions ?? false;
        // Set up navigation listener
        if (this.clearOnNavigate && typeof window !== "undefined") {
            this.setupNavigationListener();
        }
        // Set up auto-interactions
        if (this.autoInteractions && typeof window !== "undefined") {
            this.setupAutoInteractions();
        }
    }
    // =============================================================================
    // Configuration API
    // =============================================================================
    /** Get the unique session ID */
    getSessionId() {
        return this.sessionId;
    }
    /** Check if an interaction is active */
    isActive() {
        return this.currentInteractionId !== "";
    }
    /** Get the current action name */
    getCurrentAction() {
        return this.currentAction;
    }
    /** Enable or disable */
    setEnabled(enabled) {
        this.enabled = enabled;
    }
    /** Configure bolt */
    configure(config) {
        if (config.maxEvents !== undefined) {
            this.storage = new BoltStorage(config.maxEvents);
        }
        if (config.clearOnNavigate !== undefined) {
            this.clearOnNavigate = config.clearOnNavigate;
        }
        if (config.autoInteractions !== undefined) {
            this.autoInteractions = config.autoInteractions;
            if (this.autoInteractions) {
                this.setupAutoInteractions();
            }
        }
    }
    // =============================================================================
    // Component Watching API
    // =============================================================================
    /**
     * Set components to watch for render tracking.
     * Only these components (and their parent chains) will be logged.
     */
    watchComponents(componentNames) {
        this.watchedComponents.clear();
        for (const name of componentNames) {
            this.watchedComponents.add(name);
        }
        console.log(`[bolt] Watching: ${componentNames.join(", ") || "(none)"}`);
    }
    /** Add a single component to watch */
    watch(componentName) {
        this.watchedComponents.add(componentName);
        console.log(`[bolt] +watch: ${componentName}`);
    }
    /** Remove from watch list */
    unwatch(componentName) {
        if (componentName) {
            this.watchedComponents.delete(componentName);
        }
        else {
            this.watchedComponents.clear();
        }
    }
    /** Get watched components */
    getWatchedComponents() {
        return Array.from(this.watchedComponents);
    }
    /** Enable always-track mode (track even without active interaction) */
    setAlwaysTrack(enabled) {
        this.alwaysTrackWatched = enabled;
        console.log(`[bolt] Always-track: ${enabled ? "on" : "off"}`);
    }
    /** Check if always-track is enabled */
    isAlwaysTracking() {
        return this.alwaysTrackWatched;
    }
    /** Check if a component should be logged */
    shouldLogComponent(componentName) {
        if (this.watchedComponents.size === 0)
            return false;
        return this.watchedComponents.has(componentName);
    }
    // =============================================================================
    // Interaction API
    // =============================================================================
    /** Start a named interaction */
    startInteraction(action) {
        if (!this.enabled)
            return "";
        this.currentInteractionId = uuidv4();
        this.currentAction = action;
        this.interactionStartTime = Date.now();
        this.ttiMarked = false;
        this.startLongTaskObserver();
        this.emit({
            eventType: BoltEventType.Action,
            userAction: action,
            causedBy: { type: "userAction", action },
        });
        return this.currentInteractionId;
    }
    /** End the current interaction */
    endInteraction() {
        if (!this.enabled || !this.currentInteractionId)
            return;
        if (!this.ttiMarked) {
            this.markTTI();
        }
        const duration = Date.now() - this.interactionStartTime;
        this.emit({
            eventType: BoltEventType.Idle,
            durationMs: duration,
        });
        this.stopLongTaskObserver();
        this.currentInteractionId = "";
        this.currentAction = "";
    }
    /** Mark Time to Interactive */
    markTTI() {
        this.ttiMarked = true;
    }
    // =============================================================================
    // Commit Tracking
    // =============================================================================
    /** Generate a new commit ID (call at start of React commit) */
    newCommit() {
        this.currentCommitId = uuidv4();
        this.commitSequence = 0;
        return this.currentCommitId;
    }
    /** Get current commit ID */
    getCommitId() {
        return this.currentCommitId;
    }
    /** Get next commit sequence number */
    nextCommitSequence() {
        return this.commitSequence++;
    }
    // =============================================================================
    // Event Emission
    // =============================================================================
    /** Emit a bolt event */
    emit(input) {
        if (!this.enabled)
            return;
        const event = this.buildEvent(input);
        this.storage.write(event);
    }
    buildEvent(input) {
        const now = Date.now();
        return {
            // Identity & timing
            ts: now,
            sessionId: this.sessionId,
            interactionId: input.interactionId ?? (this.currentInteractionId || `orphan-${uuidv4().slice(0, 6)}`),
            spanId: uuidv4(),
            parentSpanId: input.parentSpanId ?? null,
            // UI context
            route: typeof window !== "undefined" ? window.location.pathname : "",
            pageUrl: typeof window !== "undefined" ? window.location.href : "",
            urlParamsHash: typeof window !== "undefined" ? this.hashString(window.location.search) : "",
            userAction: input.userAction ?? (this.currentAction || null),
            // Event classification
            eventType: input.eventType,
            thread: input.thread ?? EventThread.UI,
            // React context
            commitId: input.commitId ?? (this.currentCommitId || null),
            commitSequence: input.commitSequence ?? null,
            componentName: input.componentName ?? null,
            componentKey: input.componentKey ?? null,
            // Change attribution
            reasonType: input.reasonType ?? null,
            reasonKeys: input.reasonKeys ?? null,
            prevHashes: null,
            nextHashes: null,
            refOnly: input.refOnly ?? false,
            // Performance
            durationMs: input.durationMs ?? null,
            renderPhase: input.renderPhase ?? null,
            // Network
            queryName: input.queryName ?? null,
            queryId: null,
            varsHash: input.varsHash ?? "",
            varsJson: input.varsJson ?? null,
            fetchPolicy: input.fetchPolicy ?? null,
            cacheStatus: input.cacheStatus ?? null,
            requestId: null,
            responseSize: input.responseSize ?? null,
            // Causation
            causedBy: input.causedBy ?? null,
        };
    }
    // =============================================================================
    // Specialized Logging Methods
    // =============================================================================
    /** Log a render event */
    logRender(opts) {
        this.emit({
            eventType: BoltEventType.Render,
            componentName: opts.componentName,
            componentKey: opts.componentKey,
            parentSpanId: opts.parentSpanId,
            commitId: opts.commitId,
            commitSequence: opts.commitSequence,
            durationMs: opts.durationMs,
            reasonType: opts.reasonType,
            reasonKeys: opts.reasonKeys,
            refOnly: opts.refOnly,
            renderPhase: opts.renderPhase,
            causedBy: opts.causedBy,
        });
    }
    /** Log a query start */
    logQueryStart(opts) {
        this.emit({
            eventType: BoltEventType.QueryStart,
            thread: EventThread.Net,
            queryName: opts.queryName,
            varsHash: this.hashObject(opts.variables),
            varsJson: opts.variables,
            fetchPolicy: opts.fetchPolicy,
            causedBy: opts.causedBy,
        });
    }
    /** Log a query resolve */
    logQueryResolve(opts) {
        this.emit({
            eventType: BoltEventType.QueryResolve,
            thread: EventThread.Net,
            queryName: opts.queryName,
            durationMs: opts.durationMs,
            varsHash: this.hashObject(opts.variables),
            varsJson: opts.variables,
            cacheStatus: opts.cacheStatus,
            responseSize: opts.responseSize,
        });
    }
    /** Log a URL change */
    logUrlWrite(changedParams) {
        this.emit({
            eventType: BoltEventType.UrlWrite,
            reasonType: { type: "url", params: changedParams },
            reasonKeys: changedParams,
        });
    }
    /** Log a context change */
    logContextWrite(contextName, changedKeys, causedBy) {
        this.emit({
            eventType: BoltEventType.ContextWrite,
            componentName: contextName,
            reasonType: { type: "context", name: contextName, keys: changedKeys },
            reasonKeys: changedKeys,
            causedBy,
        });
    }
    /** Log a commit */
    logCommit(opts) {
        this.newCommit();
        this.emit({
            eventType: BoltEventType.Commit,
            durationMs: opts.durationMs,
        });
    }
    // =============================================================================
    // Query & Export API
    // =============================================================================
    /** Get all events */
    getEvents() {
        return this.storage.getAll();
    }
    /** Get events for current interaction */
    getCurrentEvents() {
        return this.storage.getByInteraction(this.currentInteractionId);
    }
    /** Clear all events */
    clear() {
        this.storage.clear();
    }
    /** Export as JSON */
    exportJson() {
        return JSON.stringify(this.storage.getAll(), null, 2);
    }
    /** Get stats */
    getStats() {
        return this.storage.getStats();
    }
    /** Get formatted report */
    getReport() {
        const events = this.storage.getAll();
        const lines = [`=== BOLT REPORT (${this.sessionId}) ===`, ""];
        // Group by interaction
        const byInteraction = new Map();
        for (const e of events) {
            const list = byInteraction.get(e.interactionId) ?? [];
            list.push(e);
            byInteraction.set(e.interactionId, list);
        }
        // Show last interaction
        const interactions = Array.from(byInteraction.entries());
        if (interactions.length > 0) {
            const [id, evts] = interactions[interactions.length - 1];
            lines.push(`Last Interaction: ${id}`);
            lines.push(`Events: ${evts.length}`);
            lines.push("");
            lines.push("TIMELINE:");
            for (const e of evts.slice(-30)) {
                const comp = e.componentName ? `[${e.componentName}]` : "";
                const query = e.queryName ? `[${e.queryName}]` : "";
                const dur = e.durationMs ? `(${Math.round(e.durationMs)}ms)` : "";
                lines.push(`  ${e.eventType} ${comp}${query} ${dur}`.trim());
            }
        }
        // Summary
        const stats = this.storage.getStats();
        lines.push("", "SUMMARY:");
        for (const [type, count] of Object.entries(stats.byType)) {
            lines.push(`  ${type}: ${count}`);
        }
        return lines.join("\n");
    }
    // =============================================================================
    // Private Helpers
    // =============================================================================
    hashString(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }
        return hash.toString(36);
    }
    hashObject(obj) {
        if (!obj)
            return "";
        try {
            return this.hashString(JSON.stringify(obj));
        }
        catch {
            return "";
        }
    }
    startLongTaskObserver() {
        if (typeof PerformanceObserver === "undefined")
            return;
        // Clean up any existing observer to prevent duplicate events
        this.stopLongTaskObserver();
        try {
            this.longTaskObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    this.emit({
                        eventType: BoltEventType.LongTask,
                        durationMs: entry.duration,
                    });
                }
            });
            this.longTaskObserver.observe({ entryTypes: ["longtask"] });
        }
        catch {
            // Not supported
        }
    }
    stopLongTaskObserver() {
        this.longTaskObserver?.disconnect();
        this.longTaskObserver = null;
    }
    setupNavigationListener() {
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        history.pushState = (...args) => {
            this.onNavigate();
            return originalPushState.apply(history, args);
        };
        history.replaceState = (...args) => {
            this.onNavigate();
            return originalReplaceState.apply(history, args);
        };
        window.addEventListener("popstate", () => this.onNavigate());
    }
    onNavigate() {
        this.pageUrl = window.location.href;
        if (this.clearOnNavigate) {
            this.clear();
        }
    }
    setupAutoInteractions() {
        // Auto-start interactions on click
        document.addEventListener("click", (e) => {
            const target = e.target;
            const action = target.dataset?.action ?? target.tagName.toLowerCase();
            // End previous interaction if active
            if (this.isActive()) {
                this.endInteraction();
            }
            this.startInteraction(`click.${action}`);
            // Auto-end after idle
            setTimeout(() => {
                if (this.isActive() && this.currentAction === `click.${action}`) {
                    this.endInteraction();
                }
            }, 5000);
        }, { capture: true });
    }
}
// =============================================================================
// Singleton & Exports
// =============================================================================
const IS_DEV = typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : true;
/** Default Bolt instance */
export const bolt = new Bolt();
// Expose globally in dev
if (IS_DEV && typeof window !== "undefined") {
    window.bolt = bolt;
    console.log("[bolt] Ready. Try: bolt.watchComponents(['MyComponent']), bolt.startInteraction('test')");
}
/** Create a new Bolt instance */
export function createBolt(config) {
    return new Bolt(config);
}
//# sourceMappingURL=Bolt.js.map